import { Routes, Route } from 'react-router-dom'
import { NavigateWithThread } from '../navigate-with-thread'
import { FocusDashboard } from '../focus-dashboard'
import { AdminLayout } from '../admin/admin-layout'
import { AdminUserDetailPage } from '../admin/admin-user-detail-page'
import { AdminUsersPage } from '../admin/users-page'
import { AdminReportingPage } from '../admin/reporting-page'
import { AdminOrganisationsPage } from '../admin/organisations-page'
import { AdminMarketplaceIngestPage } from '../admin/marketplace-ingest-page'
import { EvaluationRubricsPage } from '../admin/evaluation-rubrics/evaluation-rubrics-page'
import { EvaluationRubricDetailPage } from '../admin/evaluation-rubrics/evaluation-rubric-detail-page'
import { PlatformSimulationPage } from '../platform/platform-simulation-page'
import { ClassifierCorporaPage } from '../admin/classifiers/classifier-corpora-page'
import { ClassifierBuilderPage } from '../admin/classifiers/classifier-builder-page'
import { SkillsCatalogPage } from '../admin/skills-catalog/skills-catalog-page'
import { UserInviteForm } from '../admin/user-invite-form'
import { AdminWaitlistPage } from '../admin/waitlist-page'
import { accessRequestEnabled, getSignupMode } from '@/constants/signup'
import { NoticesPage } from '../notices/notices-page'
import { AdminFeedbackPage } from '../admin/feedback/admin-feedback-page'
import { AdminFeedbackPromptsPage } from '../admin/feedback/admin-feedback-prompts-page'
import { isProductFeedbackEnabled } from '@/constants'
import { isPlatformModuleEnabled } from '@/lib/platform-modules'

interface PlatformRoutesProps {
  onStartConversation?: (message: string) => void
}

export function PlatformRoutes({ onStartConversation }: PlatformRoutesProps) {
  return (
    <Routes>
      <Route
        index
        element={
          <FocusDashboard
            focus="platform"
            onStartConversation={onStartConversation}
          />
        }
      />
      {isPlatformModuleEnabled('users') && (
        <Route
          path="users"
          element={
            <AdminLayout>
              <AdminUsersPage basePath="/platform" />
            </AdminLayout>
          }
        />
      )}
      {isPlatformModuleEnabled('users') && (
        <Route
          path="users/:userId"
          element={
            <AdminLayout>
              <AdminUserDetailPage basePath="/platform" />
            </AdminLayout>
          }
        />
      )}
      {(accessRequestEnabled() || getSignupMode() === 'approval') && (
        <Route
          path="waitlist"
          element={
            <AdminLayout>
              <AdminWaitlistPage />
            </AdminLayout>
          }
        />
      )}
      {isPlatformModuleEnabled('users') && (
        <Route
          path="invite"
          element={
            <AdminLayout>
              <UserInviteForm onSuccess={() => window.history.back()} platformMode />
            </AdminLayout>
          }
        />
      )}
      {isPlatformModuleEnabled('organisations') && (
        <Route
          path="organisations"
          element={
            <AdminLayout>
              <AdminOrganisationsPage basePath="/platform" />
            </AdminLayout>
          }
        />
      )}
      {isPlatformModuleEnabled('engagement') && (
        <Route
          path="engagement"
          element={
            <AdminLayout>
              <AdminReportingPage
                headerPath="/platform/engagement"
                title="Engagement"
                description="Monitor user engagement, entity statistics, and activity trends"
              />
            </AdminLayout>
          }
        />
      )}
      {isPlatformModuleEnabled('marketplace') && (
        <Route
          path="marketplace-ingest"
          element={
            <AdminLayout>
              <AdminMarketplaceIngestPage />
            </AdminLayout>
          }
        />
      )}
      {isPlatformModuleEnabled('rubrics') && (
        <Route
          path="evaluation-rubrics"
          element={
            <AdminLayout>
              <EvaluationRubricsPage />
            </AdminLayout>
          }
        />
      )}
      {isPlatformModuleEnabled('rubrics') && (
        <Route
          path="evaluation-rubrics/:corpusId"
          element={
            <AdminLayout>
              <EvaluationRubricDetailPage onStartConversation={onStartConversation} />
            </AdminLayout>
          }
        />
      )}
      {isPlatformModuleEnabled('simulation') && (
        <>
          <Route
            path="simulation"
            element={
              <AdminLayout>
                <PlatformSimulationPage />
              </AdminLayout>
            }
          />
          <Route
            path="simulation/:scenarioId"
            element={
              <AdminLayout>
                <PlatformSimulationPage />
              </AdminLayout>
            }
          />
        </>
      )}
      {isPlatformModuleEnabled('classifiers') && (
        <Route
          path="classifiers"
          element={
            <AdminLayout>
              <ClassifierCorporaPage />
            </AdminLayout>
          }
        />
      )}
      {isPlatformModuleEnabled('classifiers') && (
        <Route
          path="classifiers/:corpusId"
          element={
            <AdminLayout>
              <ClassifierBuilderPage />
            </AdminLayout>
          }
        />
      )}
      {isPlatformModuleEnabled('skills') && (
        <Route
          path="skills-catalog"
          element={
            <AdminLayout>
              <SkillsCatalogPage />
            </AdminLayout>
          }
        />
      )}
      <Route
        path="notices"
        element={
          <AdminLayout>
            <NoticesPage />
          </AdminLayout>
        }
      />
      {isProductFeedbackEnabled() && isPlatformModuleEnabled('feedback') && (
        <>
          <Route
            path="feedback"
            element={
              <AdminLayout>
                <AdminFeedbackPage />
              </AdminLayout>
            }
          />
          <Route
            path="feedback/prompts"
            element={
              <AdminLayout>
                <AdminFeedbackPromptsPage />
              </AdminLayout>
            }
          />
        </>
      )}
      <Route path="*" element={<NavigateWithThread to="" />} />
    </Routes>
  )
}
